import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { Table, TableProps } from 'antd';
import { blue, red, orange } from '@ant-design/colors';

export type SharedTableProps = TableProps<any> & {
	cursor?: string;
};

export function SharedTable(props: SharedTableProps): JSX.Element {
	const [selectedRow, setSelectedRow] = useState<string | null>(null);
	const navigateTimeout = useRef<NodeJS.Timeout | null>(null);
	const location = useLocation();

	useEffect(() => {
		if (navigateTimeout.current !== null) clearTimeout(navigateTimeout.current);

		const rowId = location.hash.replace('#', '');
		const rowSelector = `tr[data-row-key="${rowId}"]`;
		const rowELement = document.querySelector(rowSelector);

		setSelectedRow(rowELement === null ? null : rowSelector);
		if (rowELement === null) return;

		navigateTimeout.current = setTimeout(() => {
			rowELement.scrollIntoView({ behavior: 'auto', block: 'center' });
		}, 100);
	}, [props.dataSource, location.hash]);

	return (
		<StyledTable
			selectedRow={selectedRow}
			pagination={{ position: [], pageSize: 10_000 }}
			sticky
			scroll={{ x: 500 }}
			{...props}
		/>
	);
}

const StyledTable = styled(Table)<SharedTableProps & { selectedRow: string | null }>`
	height: 100%;

	thead th {
		font-size: 0.8rem;
		padding: 0.4rem 1rem;
	}

	thead tr th {
		background-color: #f6f6f6;
	}

	tbody td {
		color: black;
		padding: 0.8rem 1rem;
		font-size: 0.8rem;
	}

	.ant-spin > * {
		font-size: 1.2rem;
	}

	tr:nth-child(odd) {
		background: #fdfdfd;
	}

	tr.ant-table-row > td {
		cursor: ${({ cursor }) => cursor ?? 'pointer'};
	}

	tr td:first-child {
		border-left: 3px solid transparent;
	}

	${({ selectedRow }) => {
		if (selectedRow === null) return;

		return `
			${selectedRow} {
				td {
					border-top: 1px solid dodgerblue;
					border-bottom: 1px solid dodgerblue;
				}
			}
		`;
	}}

	tr.ant-table-row.red-row-highlight {
		td {
			background: ${red[0]};
		}

		td:first-child {
			border-left: 3px solid ${red[4]};
		}
	}

	tr.ant-table-row.orange-row-highlight {
		td {
			background: ${orange[0]};
		}

		td:first-child {
			border-left: 3px solid ${orange[4]};
		}
	}

	tr.ant-table-row:hover {
		td {
			background: ${blue[0]};
		}

		td:first-child {
			border-left: 3px solid ${blue[4]};
		}
	}

	.ant-spin-nested-loading,
	.ant-spin-container,
	.ant-table-empty,
	.ant-table-container,
	.ant-table-body,
	.ant-table-body table {
		height: 100%;
	}
`;
